import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { SortSelect } from "@/components/SortSelect";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const sort = params.get("sort") || "featured";
  const [term, setTerm] = useState(q);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setTerm(q); }, [q]);

  useEffect(() => {
    if (!q) { setProducts([]); setLoading(false); return; }
    setLoading(true);
    api.get(`/products?q=${encodeURIComponent(q)}&sort=${sort}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [q, sort]);

  const submit = (e) => {
    e.preventDefault();
    const val = term.trim();
    if (val) setParams(sort === "featured" ? { q: val } : { q: val, sort });
  };

  const setSort = (v) => setParams(v === "featured" ? { q } : { q, sort: v }, { replace: true });

  return (
    <div className="pt-28 pb-24 max-w-[1440px] mx-auto px-4 md:px-8 min-h-[70vh]" data-testid="search-page">
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-6">Search</h1>
      <form onSubmit={submit} className="flex items-center gap-3 border-b border-ink pb-3 mb-10 max-w-2xl">
        <SearchIcon size={20} className="text-gray-400" />
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search for gear..."
          className="flex-1 bg-transparent text-lg focus:outline-none"
          data-testid="search-page-input"
        />
        <button type="submit" className="text-xs font-bold uppercase tracking-widest hover:text-sale" data-testid="search-page-submit">Search</button>
      </form>

      {q && (
        <div className="flex justify-between items-center mb-8 gap-4 flex-wrap">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500" data-testid="search-results-count">
            {loading ? "Searching..." : `${products.length} result${products.length === 1 ? "" : "s"} for "${q}"`}
          </p>
          {products.length > 0 && <SortSelect value={sort} onChange={setSort} />}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] bg-gray-100 animate-pulse" />)}
        </div>
      ) : !q ? (
        <p className="text-gray-400 uppercase tracking-widest">Type something to search the store.</p>
      ) : products.length === 0 ? (
        <p className="text-center py-20 uppercase tracking-widest text-gray-400" data-testid="search-empty">No matches for "{q}". Try another term.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6 md:gap-y-12">
          {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
